import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PurchaseHubTab = () => {
  const [selectedPurchaseType, setSelectedPurchaseType] = useState('all');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const purchaseOptions = [
    {
      id: 'sugar',
      type: 'Sugar Only',
      icon: 'Package',
      description: 'Purchase sugar without transportation',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      features: [
        'Choose from available quotas',
        'Multiple factories to choose from',
        'Various grades available (S-30, M-30, L-30)',
        'Competitive pricing per MT',
        'Direct factory delivery',
        'Quality assured'
      ],
      sample: {
        factory: 'Shree Ganesh Sugar Mills',
        grade: 'S-30',
        quantity: '500 MT',
        pricePerMT: '₹38,500',
        total: '₹1,92,50,000'
      }
    },
    {
      id: 'bundle',
      type: 'Sugar + Truck',
      icon: 'Boxes',
      description: 'Complete solution with sugar and transportation',
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
      features: [
        'End-to-end solution',
        'Sugar + Transportation in one booking',
        'Pre-negotiated transporter rates',
        'GPS-enabled tracking',
        'Insurance coverage included',
        'Single invoice for everything'
      ],
      sample: {
        factory: 'Dalmia Sugar Mills',
        grade: 'S-31',
        quantity: '300 MT',
        sugarPrice: '₹39,200/MT',
        transportRate: '₹850/MT',
        total: '₹1,20,15,000'
      }
    },
    {
      id: 'truck',
      type: 'Truck Only',
      icon: 'Truck',
      description: 'Transportation services only',
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      features: [
        'Book verified transporters',
        'Multiple vehicle types available',
        'Flexible distance pricing',
        'GPS tracking included',
        'Insurance options',
        'Direct transporter contact'
      ],
      sample: {
        transporter: 'Swift Freight Services',
        vehicleType: '32-Foot Truck',
        capacity: '25 MT',
        distance: '0-400 km',
        rate: '₹820/MT',
        estimatedCost: '₹20,500'
      }
    }
  ];

  const recentPurchases = [
    {
      id: 1,
      type: 'Sugar + Truck',
      factory: 'Shree Ganesh Sugar Mills',
      grade: 'S-30',
      quantity: 500,
      transporter: 'SafeLoad Logistics',
      totalAmount: 19500000,
      date: '2025-11-25',
      status: 'completed',
      destination: 'Mumbai, Maharashtra'
    },
    {
      id: 2,
      type: 'Sugar Only',
      factory: 'Bannari Amman Sugars',
      grade: 'M-30',
      quantity: 300,
      totalAmount: 11400000,
      date: '2025-11-28',
      status: 'in-progress',
      destination: 'Pune, Maharashtra'
    },
    {
      id: 3,
      type: 'Truck Only',
      transporter: 'Express Transport Solutions',
      vehicleType: '32-Foot Truck',
      quantity: 25,
      totalAmount: 22500,
      date: '2025-11-30',
      status: 'pending',
      origin: 'Kolhapur',
      destination: 'Sangli'
    }
  ];

  const filteredPurchases = selectedPurchaseType === 'all' 
    ? recentPurchases 
    : recentPurchases?.filter(p => {
        if (selectedPurchaseType === 'sugar') return p?.type === 'Sugar Only';
        if (selectedPurchaseType === 'bundle') return p?.type === 'Sugar + Truck';
        if (selectedPurchaseType === 'truck') return p?.type === 'Truck Only';
        return true;
      });

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in-progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* Purchase Options */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Purchase Options</h3>
          <p className="text-sm text-muted-foreground">
            Choose from our flexible purchasing options based on your needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {purchaseOptions?.map((option) => (
            <div
              key={option?.id}
              className="bg-background rounded-lg border-2 border-border p-6 hover:border-[#ff8c00] hover:shadow-lg transition-all cursor-pointer"
              onClick={() => {
                setSelectedOption(option);
                setShowPurchaseModal(true);
              }}
            >
              <div className={`w-16 h-16 rounded-lg ${option?.bgColor} flex items-center justify-center mb-4`}>
                <Icon name={option?.icon} size={32} className={option?.iconColor} />
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">{option?.type}</h4>
              <p className="text-sm text-muted-foreground mb-4">{option?.description}</p>
              
              <div className="space-y-2 mb-4">
                {option?.features?.slice(0, 3)?.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <Icon name="Check" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                className="w-full bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
                onClick={(e) => {
                  e?.stopPropagation();
                  setSelectedOption(option);
                  setShowPurchaseModal(true);
                }}
              >
                <Icon name="ShoppingCart" size={18} />
                <span>Purchase Now</span>
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Purchases */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Recent Purchases</h3>
            <p className="text-sm text-muted-foreground">Track your recent transactions</p>
          </div>
          <div className="flex gap-2">
            {['all', 'sugar', 'bundle', 'truck']?.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedPurchaseType(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedPurchaseType === type
                    ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                {type === 'all' ? 'All' : type?.charAt(0)?.toUpperCase() + type?.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {filteredPurchases?.map((purchase) => (
            <div key={purchase?.id} className="bg-background rounded-lg border border-border p-6 hover:shadow-lg transition-all">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      {purchase?.type}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(purchase?.status)}`}>
                      {purchase?.status?.replace('-', ' ')?.toUpperCase()}
                    </span>
                  </div>

                  {purchase?.type === 'Sugar + Truck' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Factory</p>
                        <p className="font-medium text-foreground">{purchase?.factory}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Grade</p>
                        <p className="font-medium text-foreground">{purchase?.grade}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Quantity</p>
                        <p className="font-medium text-foreground">{purchase?.quantity} MT</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Transporter</p>
                        <p className="font-medium text-foreground">{purchase?.transporter}</p>
                      </div>
                    </div>
                  )}

                  {purchase?.type === 'Sugar Only' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Factory</p>
                        <p className="font-medium text-foreground">{purchase?.factory}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Grade</p>
                        <p className="font-medium text-foreground">{purchase?.grade}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Quantity</p>
                        <p className="font-medium text-foreground">{purchase?.quantity} MT</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Destination</p>
                        <p className="font-medium text-foreground">{purchase?.destination}</p>
                      </div>
                    </div>
                  )}

                  {purchase?.type === 'Truck Only' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Transporter</p>
                        <p className="font-medium text-foreground">{purchase?.transporter}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Vehicle</p>
                        <p className="font-medium text-foreground">{purchase?.vehicleType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Origin</p>
                        <p className="font-medium text-foreground">{purchase?.origin}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Destination</p>
                        <p className="font-medium text-foreground">{purchase?.destination}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-6 mt-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">
                        {new Date(purchase.date)?.toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="IndianRupee" size={16} className="text-green-600" />
                      <span className="font-semibold text-green-600">
                        ₹{purchase?.totalAmount?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex lg:flex-col gap-2 lg:w-40">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {}}
                  >
                    <Icon name="Eye" size={16} />
                    <span>View</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {}}
                  >
                    <Icon name="Download" size={16} />
                    <span>Invoice</span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredPurchases?.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
              <Icon name="ShoppingCart" size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Purchases Found</h3>
            <p className="text-muted-foreground mb-4">Start by making your first purchase</p>
            <Button
              className="bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
              onClick={() => setShowPurchaseModal(true)}
            >
              <Icon name="Plus" size={18} />
              <span>Make a Purchase</span>
            </Button>
          </div>
        )}
      </div>
      {/* Purchase Modal */}
      {showPurchaseModal && selectedOption && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg ${selectedOption?.bgColor} flex items-center justify-center`}>
                  <Icon name={selectedOption?.icon} size={24} className={selectedOption?.iconColor} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">{selectedOption?.type}</h3>
                  <p className="text-sm text-muted-foreground">{selectedOption?.description}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowPurchaseModal(false);
                  setSelectedOption(null);
                }}
                className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="font-semibold text-foreground mb-3">Key Features</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedOption?.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Icon name="CheckCircle2" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background rounded-lg border border-border p-4 mb-6">
                <h4 className="font-semibold text-foreground mb-3">Sample Calculation</h4>
                <div className="space-y-2 text-sm">
                  {Object.entries(selectedOption?.sample)?.map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-muted-foreground capitalize">
                        {key?.replace(/([A-Z])/g, ' $1')?.trim()}:
                      </span>
                      <span className="font-medium text-foreground">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white"
                  onClick={() => {
                    // Redirect to appropriate section
                    if (selectedOption?.id === 'sugar') {
                      // Navigate to Available Quota tab or Sugar Marketplace
                      console.log('Navigate to sugar purchase');
                    } else if (selectedOption?.id === 'bundle') {
                      // Navigate to bundle purchase
                      console.log('Navigate to bundle purchase');
                    } else if (selectedOption?.id === 'truck') {
                      // Navigate to truck marketplace
                      console.log('Navigate to truck purchase');
                    }
                    setShowPurchaseModal(false);
                  }}
                >
                  <Icon name="ArrowRight" size={18} />
                  <span>Proceed to Purchase</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPurchaseModal(false);
                    setSelectedOption(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseHubTab;